import React, { useEffect, useMemo, useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { prefetchForPath } from "../prefetchAppChunks";

const SidebarMenu = ({ isCollapsed, expandSidebar }) => {
    const location = useLocation();
    const user = useSelector(state => state.userReducer.user);

    const menuItems = useMemo(() => ([
        {
            id: 0,
            name: 'Dashboard',
            path: '/dashboard',
            subNav: [],
            key: 'dashboard',
            roles: ['admin', 'agent', 'customer'],
        },
        {
            id: 1,
            name: 'Tickets',
            path: '/tickets',
            key: 'tickets',
            roles: ['admin', 'agent', 'customer'],
            subNav: [
                { id: -1, name: 'All Tickets', path: '/tickets', key: 'tickets_all' },
                { id: 0, name: 'Open', path: '/tickets/open', key: 'tickets_open' },
                { id: 1, name: 'Active', path: '/tickets/active', key: 'tickets_active' },
                { id: 2, name: 'Unassigned', path: '/tickets/unassigned', key: 'tickets_unassigned' },
                { id: 3, name: 'My Tickets', path: '/tickets/my', key: 'tickets_my' },
                { id: 4, name: 'Critical', path: '/tickets/critical?priority=16&sort_by=created_at&sort_direction=desc&page=1&per_page=10', key: 'tickets_critical' },
                { id: 5, name: 'High', path: '/tickets/high?priority=15&sort_by=created_at&sort_direction=desc&page=1&per_page=10', key: 'tickets_high' },
                { id: 6, name: 'Need Confirmation', path: '/tickets/need_confirmation?status=need+confirmation&sort_by=created_at&sort_direction=desc&page=1&per_page=10', key: 'tickets_need_confirmation' },
                { id: 7, name: 'Need Clarification', path: '/tickets/need_clarification?status=need+clarification&sort_by=created_at&sort_direction=desc&page=1&per_page=10', key: 'tickets_need_clarification' },
                { id: 13, name: 'Needs More Details', path: '/tickets/needs_more_details?status=needs+more+details&sort_by=created_at&sort_direction=desc&page=1&per_page=10', key: 'tickets_needs_more_details' },
                { id: 14, name: 'Rejected', path: '/tickets/rejected', key: 'tickets_rejected' },
                { id: 15, name: 'Overdue', path: '/tickets/overdue', key: 'tickets_overdue' },
                { id: 8, name: 'Pending', path: '/tickets/pending', key: 'tickets_pending' },
                { id: 9, name: 'Assigned', path: '/tickets/assigned', key: 'tickets_assigned' },
                { id: 10, name: 'Closed', path: '/tickets/closed', key: 'tickets_closed' },
                { id: 11, name: 'Spam', path: '/tickets/spam', key: 'tickets_spam' },
                { id: 12, name: 'Deleted', path: '/tickets/deleted', key: 'tickets_deleted' },
            ]
        },
        {
            id: 10,
            name: 'Transferred Tickets',
            path: '/tickets/transferred',
            subNav: [],
            key: 'transferred_tickets',
            roles: ['admin', 'agent'],
        },
        {
            id: 2,
            name: 'Users',
            path: '/users',
            subNav: [],
            key: 'users',
            roles: ['admin'],
        },
        {
            id: 3,
            name: 'Departments',
            path: '/departments',
            subNav: [],
            key: 'departments',
            roles: ['admin'],
        },
        {
            id: 4,
            name: 'Incoming Emails',
            path: '/emails',
            subNav: [],
            key: 'emails',
            roles: ['admin'],
        },
        {
            id: 5,
            name: 'Reports',
            path: '/reports/overview',
            key: 'reports',
            roles: ['admin', 'agent'],
            subNav: [
                { id: 0, name: 'Overview', path: '/reports/overview', key: 'reports_overview' },
                { id: 1, name: 'Activity', path: '/reports/activity', key: 'reports_activity' },
                { id: 2, name: 'Tickets', path: '/reports/tickets', key: 'reports_tickets' },
                { id: 4, name: 'Time', path: '/reports/time', key: 'reports_time' }
            ]
        },
        {
            id: 6,
            name: 'Settings',
            path: '/settings',
            key: 'settings',
            roles: ['admin', 'customer', 'agent'],
            subNav: [
                { id: 0, name: 'Types', path: '/settings/type', key: 'settings_type', roles: ['admin'] },
                { id: 1, name: 'Priorities', path: '/settings/priority', key: 'settings_priority', roles: ['admin'] },
                { id: 2, name: 'Tags', path: '/settings/tags', key: 'settings_tags', roles: ['admin'] },
                { id: 5, name: 'Quick Replies', path: '/settings/quick-replies', key: 'settings_quick_replies', roles: ['admin', 'agent'] },
                { id: 6, name: 'Transfering Codes', path: '/settings/transfering-codes', key: 'settings_transfering_codes', roles: ['admin'] },
                { id: 3, name: 'Apps', path: '/settings/connect_cc', key: 'settings_connect_cc', roles: ['admin'] },
                { id: 4, name: 'Notifications', path: '/notification-preferences', key: 'notifications', roles: ['admin', 'customer', 'agent'] },
            ]
        },
        {
            id: 7,
            name: 'Subscription',
            path: '/subscription',
            key: 'subscription',
            roles: ['admin'],
            subNav: [
                { id: 0, name: 'Current Plan', path: '/subscription/plan', key: 'subscription_plan' },
                { id: 1, name: 'Payment Method', path: '/subscription/payment', key: 'subscription_payment' },
                { id: 2, name: 'Invoice History', path: '/subscription/invoice', key: 'subscription_invoice' }
            ]
        },
        {
            id: 8,
            name: 'Embed Ticket Forms',
            path: '/embed-ticket-forms',
            key: 'embed_ticket_forms',
            roles: ['admin'],
            subNav: [
                { id: 0, name: 'Forms', path: '/embed-ticket-forms', key: 'embed_ticket_forms_forms' },
                {
                    id: 1,
                    name: 'Trusted Domains',
                    path: '/embed-ticket-forms/trusted-domains',
                    key: 'embed_ticket_forms_trusted_domains',
                },
            ]
        },
        {
            id: 9,
            name: 'Developer Options',
            path: '/developer',
            key: 'developer',
            roles: ['admin'],
            subNav: [],
        }
    ]), []);

    const isCustomer = user?.isCustomer || user?.is_customer;
    const isAdmin = user?.isAdmin || user?.is_admin;
    const isAgent = user?.isAgent || user?.is_agent;

    const isNavVisible = (nav, parentRoles) => {
        const roles = nav.roles || parentRoles || [];
        if (isCustomer) return roles.includes('customer');
        if (isAdmin) return roles.includes('admin');
        if (isAgent) return roles.includes('agent');
        return false;
    };

    const visibleMenuItems = useMemo(
        () => menuItems.filter(isNavVisible),
        [menuItems, user]
    );

    const getRouteState = () => {
        const pathname = location.pathname;

        // Prefer longest exact path so `/tickets/transferred` (top-level) wins over `/tickets`.
        const exactMatches = [];
        for (const item of visibleMenuItems) {
            if (item.subNav?.length) {
                for (const sub of item.subNav) {
                    const subPath = sub.path.split('?')[0];
                    if (pathname === subPath) {
                        exactMatches.push({
                            length: subPath.length,
                            activeParentKey: item.key,
                            activeChildKey: sub.key,
                            autoOpenedKey: item.key,
                        });
                    }
                }
            }

            const itemPath = item.path.split('?')[0];
            if (pathname === itemPath) {
                exactMatches.push({
                    length: itemPath.length,
                    activeParentKey: item.key,
                    activeChildKey: null,
                    autoOpenedKey: item.subNav?.length ? item.key : '',
                });
            }
        }

        if (exactMatches.length) {
            exactMatches.sort((a, b) => b.length - a.length);
            const best = exactMatches[0];
            return {
                activeParentKey: best.activeParentKey,
                activeChildKey: best.activeChildKey,
                autoOpenedKey: best.autoOpenedKey,
            };
        }

        for (const item of visibleMenuItems) {
            const itemPath = item.path.split('?')[0];
            if (pathname.startsWith(`${itemPath}/`)) {
                return {
                    activeParentKey: item.key,
                    activeChildKey: null,
                    autoOpenedKey: item.subNav?.length ? item.key : ''
                };
            }
        }

        return {
            activeParentKey: 'dashboard',
            activeChildKey: null,
            autoOpenedKey: ''
        };
    };

    const routeState = useMemo(() => getRouteState(), [location.pathname, visibleMenuItems]);

    const [openedKey, setOpenedKey] = useState(isCollapsed ? '' : routeState.autoOpenedKey);

    useEffect(() => {
		if (isCollapsed) {
			setOpenedKey('');
		} else if (routeState.autoOpenedKey) {
			setOpenedKey(routeState.autoOpenedKey);
		}
	}, [routeState.autoOpenedKey, isCollapsed]);


    const handleParentClick = (nav) => {
		const hasChildren = nav.subNav?.length > 0;

		if (isCollapsed) {
			if (hasChildren) {
				expandSidebar();
				setOpenedKey(nav.key);
			}
			return;
		}

		if (!hasChildren) return;

		setOpenedKey(prev => (prev === nav.key ? '' : nav.key));
	};

    return (
        <ul className="nav flex-column parent-ul-container">
            {visibleMenuItems.map(nav => {
                const hasChildren = nav.subNav?.length > 0;
                const isActiveParent = nav.key === routeState.activeParentKey;
                const isOpened = nav.key === openedKey;

                return (
                    <li
                        key={nav.id}
                        className={`nav-item parent-li-item ${isActiveParent ? 'active' : ''} ${isOpened ? 'show-children' : ''}`}
                    >
                        {hasChildren ? (
                            <button
                                type="button"
                                className="nav-link sidebar-parent-trigger"
                                onClick={() => handleParentClick(nav)}
                                onMouseEnter={() => prefetchForPath(nav.path)}
                                onFocus={() => prefetchForPath(nav.path)}
                            >
                                <span className={`new-icon-sidebar icon-${nav.key}`}></span>
                                <span className="sidebar-link-text">{nav.name}</span>
                               	{!isCollapsed && (isOpened ? <IoIosArrowUp /> : <IoIosArrowDown />)}
                            </button>
                        ) : (
                            <Link
                                className="nav-link"
                                to={nav.path}
                                onMouseEnter={() => prefetchForPath(nav.path)}
                                onFocus={() => prefetchForPath(nav.path)}
                            >
                                <span className={`new-icon-sidebar icon-${nav.key}`}></span>
                                <span className="sidebar-link-text">{nav.name}</span>
                            </Link>
                        )}

                        {hasChildren && !isCollapsed && (
                            <ul className="nav flex-column child-ul-container">
                                {nav.subNav.filter(sub => isNavVisible(sub, nav.roles)).map(sub => {
                                    const isActiveChild = sub.key === routeState.activeChildKey;

                                    return (
                                        <li
                                            key={sub.id}
                                            className={`nav-item child-li-item ${isActiveChild ? 'active' : ''}`}
                                        >
                                            <Link
                                                className="nav-link"
                                                to={sub.path}
                                                onMouseEnter={() => prefetchForPath(sub.path)}
                                                onFocus={() => prefetchForPath(sub.path)}
                                            >
                                                {sub.name}
                                            </Link>
                                            {sub.badge ? (
                                                <span className={"badge" + (sub.badge > 0 ? " danger" : "")}>
                                                    {sub.badge}
                                                </span>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default SidebarMenu;